import * as AgGrid from "ag-grid-community";
import { RefundRequest } from "../../types/refund_request";
import { escapeHtml } from "../../utils/security";

interface ReasonsCellRendererParams extends AgGrid.ICellRendererParams<RefundRequest> {
  requestReasonLabel?: string;
  reasonLabel?: string;
  statusReasonLabel?: string;
  failMessageLabel?: string;
}

const EMPTY_STRING_REGEX = /^[\s\u00A0\u2000-\u200B\u2028\u2029\u3000]*$/;
const MAX_DISPLAY_LENGTH = 30;
const MAX_TITLE_LENGTH = 300;

function normalizeValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 && !EMPTY_STRING_REGEX.test(trimmed) ? trimmed : null;
}

function truncateText(text: string, maxLength: number): string {
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}

function buildTooltipText(
  failMessage: string | null,
  statusReason: string | null,
  requestReason: string | null,
  reason: string | null,
  failMessageLabel: string,
  statusReasonLabel: string,
  requestReasonLabel: string,
  reasonLabel: string,
): string {
  const parts: string[] = [];

  if (failMessage) {
    parts.push(`${failMessageLabel}: ${failMessage}`);
  }
  if (statusReason) {
    parts.push(`${statusReasonLabel}: ${statusReason}`);
  }
  if (requestReason) {
    parts.push(`${requestReasonLabel}: ${requestReason}`);
  }
  if (reason && !requestReason) {
    parts.push(`${reasonLabel}: ${reason}`);
  }

  return parts.join("\n");
}

export function ReasonsCellRenderer(params: ReasonsCellRendererParams) {
  const data = params.data;
  if (!data?.id || !data?.payment_id || params.node?.rowPinned) {
    return;
  }

  const requestReasonLabel = params.requestReasonLabel || "Request Reason";
  const reasonLabel = params.reasonLabel || "Reason";
  const statusReasonLabel = params.statusReasonLabel || "Status Reason";
  const failMessageLabel = params.failMessageLabel || "Fail Message";

  const requestReason = normalizeValue(
    data.request_reason || (data as RefundRequest & { requested_reason?: string | null }).requested_reason,
  );
  const statusReason = normalizeValue(data.status_reason);
  const failMessage = normalizeValue(data.fail_message);
  const reason = normalizeValue(data.reason);

  const reasons = [
    { value: failMessage, label: failMessageLabel },
    { value: statusReason, label: statusReasonLabel },
    { value: requestReason, label: requestReasonLabel },
    { value: reason && !requestReason ? reason : null, label: reasonLabel },
  ];

  const selectedReason = reasons.find((r) => r.value);
  if (!selectedReason) {
    return;
  }

  const displayValue = selectedReason.value;
  const displayLabel = selectedReason.label;
  const displayText = truncateText(displayValue, MAX_DISPLAY_LENGTH);

  const tooltipText = buildTooltipText(
    failMessage,
    statusReason,
    requestReason,
    reason,
    failMessageLabel,
    statusReasonLabel,
    requestReasonLabel,
    reasonLabel,
  );

  const $el = document.createElement("div");

  const labelSpan = `<span class="reasons-cell-label">${escapeHtml(displayLabel)}:</span>`;
  const valueSpan = `<span class="reasons-cell-value">${escapeHtml(displayText)}</span>`;
  $el.innerHTML = `${labelSpan} ${valueSpan}`;

  if (tooltipText) {
    $el.title = truncateText(tooltipText, MAX_TITLE_LENGTH);
    $el.style.cursor = "help";

    $el.addEventListener("click", (e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(tooltipText).catch(() => {
        const textarea = document.createElement("textarea");
        textarea.value = tooltipText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      });
    });
  }

  return $el;
}
